import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import {
  accountKeys,
  type BillingDetailsView,
  billingAccount,
  billingDetailsQuery,
  type PutBillingDetails,
  recipientErrors,
} from "../../../api/billingAccount";
import { errorMessage } from "../../../api/client";
import { requireAdmin } from "../../../auth/useMe";
import { FormCard } from "../../../components/billing/account/FormCard";
import { type EmailEntry, InvoiceEmails } from "../../../components/billing/account/InvoiceEmails";
import { toast } from "../../../components/toast";
import { Button, ErrorState, Field, Input, Loading, Select } from "../../../components/ui";
import { US_STATES } from "../../../lib/usStates";

// Admin > Billing > Billing details (DESIGN.md "Billing (console)", Billing details tab, story
// P20): the company's legal name and EIN, the billing address (US only) and up to 5 invoice
// emails. Save sends the whole form; the server checks it and syncs Stripe in the background.

export const Route = createFileRoute("/admin/billing/details")({
  staticData: { crumb: { label: "Billing details", parents: [] } },
  beforeLoad: requireAdmin,
  loader: ({ context }) => context.queryClient.prefetchQuery(billingDetailsQuery),
  component: DetailsTab,
});

const STATE_OPTIONS = US_STATES.map((s) => ({ value: s.code, label: s.name }));

interface Form {
  legalName: string;
  taxId: string;
  line1: string;
  line2: string;
  city: string;
  state: string;
  zip: string;
  emails: EmailEntry[];
}

function formOf(d: BillingDetailsView): Form {
  return {
    legalName: d.legal_name ?? "",
    taxId: d.tax_id ?? "",
    line1: d.address.line1 ?? "",
    line2: d.address.line2 ?? "",
    city: d.address.city ?? "",
    state: d.address.state ?? "",
    zip: d.address.postal_code ?? "",
    emails: (d.recipients.length ? d.recipients : [""]).map((value, i) => ({
      key: i + 1,
      value,
    })),
  };
}

const orNull = (s: string) => (s.trim() ? s.trim() : null);

function bodyOf(f: Form): PutBillingDetails {
  return {
    legal_name: orNull(f.legalName),
    tax_id: orNull(f.taxId),
    address: {
      line1: orNull(f.line1),
      line2: orNull(f.line2),
      city: orNull(f.city),
      state: orNull(f.state),
      postal_code: orNull(f.zip),
      country: "US",
    },
    recipients: f.emails.map((e) => e.value.trim()),
  };
}

function DetailsTab() {
  const details = useQuery(billingDetailsQuery);
  return (
    <div className="flex max-w-[880px] flex-col gap-5">
      {details.isPending ? (
        <Loading />
      ) : details.isError ? (
        <ErrorState error={details.error} onRetry={() => details.refetch()} />
      ) : (
        // A save resets the form to what the server kept.
        <DetailsForm initial={details.data} />
      )}
    </div>
  );
}

function DetailsForm({ initial }: { initial: BillingDetailsView }) {
  const qc = useQueryClient();
  const [saved, setSaved] = useState(initial);
  const [form, setForm] = useState(() => formOf(initial));
  const [tried, setTried] = useState(false);
  const errors = useMemo(() => recipientErrors(form.emails.map((e) => e.value)), [form.emails]);
  const changed = JSON.stringify(bodyOf(form)) !== JSON.stringify(bodyOf(formOf(saved)));

  const save = useMutation({
    mutationFn: (body: PutBillingDetails) => billingAccount.saveDetails(body),
    onSuccess: (view) => {
      qc.setQueryData(accountKeys.details, view);
      setSaved(view);
      setForm(formOf(view));
      setTried(false);
      toast("Billing details saved.");
    },
    onError: (e) => toast(errorMessage(e), "danger"),
  });

  const set = <K extends keyof Form>(k: K, v: Form[K]) => setForm((f) => ({ ...f, [k]: v }));

  const submit = () => {
    setTried(true);
    if (errors.some((e) => e !== null)) {
      const i = errors.findIndex((e) => e !== null);
      document.querySelector<HTMLInputElement>(`input[aria-label="Invoice email ${i + 1}"]`)?.focus();
      return;
    }
    save.mutate(bodyOf(form));
  };

  const busy = save.isPending;
  return (
    <form
      className="flex flex-col gap-5"
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        submit();
      }}
    >
      <FormCard title="Company">
        <Field label="Legal name">
          <Input
            value={form.legalName}
            autoComplete="organization"
            disabled={busy}
            onChange={(e) => set("legalName", e.target.value)}
          />
        </Field>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Tax ID (EIN)" help="Shown on invoices.">
            <Input value={form.taxId} disabled={busy} onChange={(e) => set("taxId", e.target.value)} />
          </Field>
        </div>
      </FormCard>

      <FormCard title="Billing address">
        <Field label="Address">
          <Input
            value={form.line1}
            autoComplete="address-line1"
            disabled={busy}
            onChange={(e) => set("line1", e.target.value)}
          />
        </Field>
        <Field label="Address line 2">
          <Input
            value={form.line2}
            placeholder="Optional"
            autoComplete="address-line2"
            disabled={busy}
            onChange={(e) => set("line2", e.target.value)}
          />
        </Field>
        <div className="grid gap-3 sm:grid-cols-[2fr_1fr_1fr]">
          <Field label="City">
            <Input
              value={form.city}
              autoComplete="address-level2"
              disabled={busy}
              onChange={(e) => set("city", e.target.value)}
            />
          </Field>
          <Field label="State">
            <Select
              value={form.state}
              onValueChange={(v) => set("state", v)}
              options={STATE_OPTIONS}
              aria-label="State"
              disabled={busy}
            />
          </Field>
          <Field label="ZIP">
            <Input
              value={form.zip}
              inputMode="numeric"
              autoComplete="postal-code"
              disabled={busy}
              onChange={(e) => set("zip", e.target.value)}
            />
          </Field>
        </div>
      </FormCard>

      <InvoiceEmails
        entries={form.emails}
        errors={errors}
        showAll={tried}
        disabled={busy}
        onChange={(emails) => set("emails", emails)}
        onAdd={(key) => set("emails", [...form.emails, { key, value: "" }])}
      />

      <div className="flex justify-end">
        <Button type="submit" variant="primary" busy={busy} disabled={!changed}>
          Save
        </Button>
      </div>
    </form>
  );
}
